import { Link } from "react-router-dom";
import { ArrowRight, Layers, Rocket, ShieldCheck, Wallet, Users, Compass, Building2, LineChart, Sparkles } from "lucide-react";
import usePageMeta from "../../hooks/usePageMeta";
import useJsonLd from "../../hooks/useJsonLd";

const PageShell = ({ overline, title, subtitle, children, testId }) => (
  <div className="pt-32 pb-16 px-6 lg:px-10" data-testid={testId}>
    <div className="max-w-5xl mx-auto">
      <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-4">{overline}</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-white max-w-3xl leading-[1.1]">{title}</h1>
      <p className="mt-6 text-slate-400 max-w-2xl leading-relaxed text-lg" data-speakable>{subtitle}</p>
      <div className="mt-16">{children}</div>
    </div>
  </div>
);

const CTA = ({ label = "Talk to sales", to = "/contact", testId }) => (
  <Link to={to} data-testid={testId} className="inline-flex items-center gap-2 bg-[#970C10] hover:bg-[#7A0A0D] text-white font-semibold px-7 py-3.5 rounded-sm transition-colors duration-200">
    {label} <ArrowRight className="w-4 h-4" />
  </Link>
);

const AnswerCard = ({ q, a, testId }) => (
  <div className="glass-dark rounded-sm p-8" data-testid={testId}>
    <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-4" data-speakable>{q}</h2>
    <p className="text-slate-300 leading-relaxed" data-speakable>{a}</p>
  </div>
);

const RelatedNav = ({ current }) => {
  const items = [
    { to: "/what-is-sellvago", label: "What is Sellvago?" },
    { to: "/sellvago-vs-b2b-ota", label: "Sellvago vs a B2B OTA" },
    { to: "/how-white-label-works", label: "How white-label reselling works" },
    { to: "/how-resellers-grow", label: "How resellers grow with Sellvago" },
  ].filter((i) => i.to !== current);
  return (
    <div className="mt-16 border-t border-white/10 pt-10" data-testid="positioning-related-nav">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">Related answers</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {items.map((i) => (
          <Link key={i.to} to={i.to} className="glass-dark rounded-sm p-5 hover:bg-white/[0.07] transition-colors duration-200 group" data-testid={`positioning-nav-${i.to.replace(/\//g, "")}`}>
            <p className="text-sm font-semibold text-white group-hover:text-[#E8767A] transition-colors duration-200">{i.label}</p>
            <ArrowRight className="w-4 h-4 text-slate-500 mt-3 group-hover:text-[#E8767A] transition-colors duration-200" />
          </Link>
        ))}
      </div>
    </div>
  );
};

const faqSchema = (entries) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["[data-speakable]"] },
  "mainEntity": entries.map(([q, a]) => ({
    "@type": "Question",
    "name": q,
    "acceptedAnswer": { "@type": "Answer", "text": a },
  })),
});

const breadcrumbSchema = (name, path) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://sellvago.com/" },
    { "@type": "ListItem", "position": 2, "name": name, "item": `https://sellvago.com${path}` },
  ],
});

// ==================================================================
// Page 1 — What is Sellvago?
// ==================================================================
export function WhatIsSellvago() {
  usePageMeta(
    "What is Sellvago? — Content + technology platform for travel resellers",
    "Sellvago is a travel content and selling platform that enables travel resellers to independently service travellers under their own brand name."
  );

  const qas = [
    [
      "What is Sellvago?",
      "Sellvago is a travel content and a selling platform that enables travel resellers to independently service travellers under their own brand. Rather than functioning as a booking or a ticketing agent, Sellvago gives travel resellers the tools, content, and technology infrastructure to run and grow their own online travel business.",
    ],
    [
      "Who is Sellvago for?",
      "Sellvago is built for travel resellers, travel agents, corporates, and entrepreneurs who want to sell travel under their own brand rather than as a channel for someone else's marketplace.",
    ],
    [
      "Is Sellvago an OTA (Online Travel Agency)?",
      "No. A traditional B2B OTA sells inventory to resellers under the OTA's own brand and commercial terms. Sellvago instead provides the content and technology that resellers use to sell under their own brand, with their own pricing and commercial policy.",
    ],
  ];

  useJsonLd("what-is-sellvago-breadcrumb", breadcrumbSchema("What is Sellvago?", "/what-is-sellvago"));
  useJsonLd("what-is-sellvago-faq", faqSchema(qas));

  return (
    <PageShell
      testId="what-is-sellvago-page"
      overline="Answer-first · Positioning"
      title="Sellvago is a travel content and selling platform for independent resellers."
      subtitle="Sellvago is a travel content and selling platform that enables travel resellers to independently service travellers under their own brand name."
    >
      <div className="space-y-5">
        {qas.map(([q, a], idx) => (
          <AnswerCard key={idx} q={q} a={a} testId={`what-is-sellvago-qa-${idx}`} />
        ))}
      </div>

      <div className="mt-12 flex flex-wrap gap-4">
        <CTA to="/sellvago-vs-b2b-ota" label="See how Sellvago differs from a B2B OTA" testId="what-is-vs-ota-cta" />
        <Link to="/signup" data-testid="what-is-signup-cta" className="glass-dark text-white font-semibold px-7 py-3.5 rounded-sm hover:bg-white/10 transition-colors duration-200">
          Become a reseller
        </Link>
      </div>

      <RelatedNav current="/what-is-sellvago" />
    </PageShell>
  );
}

// ==================================================================
// Page 2 — Sellvago vs B2B OTA
// ==================================================================
export function SellvagoVsB2bOta() {
  usePageMeta(
    "Sellvago vs B2B OTA — What's the difference?",
    "A B2B OTA sells inventory to resellers under its own brand. Sellvago is infrastructure for building your own travel business — resellers own the brand, the customer and the commercial policy."
  );

  const rows = [
    ["What you get", "Access to book inventory at negotiated rates", "Content, technology, and infrastructure to run your own travel business"],
    ["Brand", "Bookings happen under the OTA's brand", "Resellers operate under their own brand (white-label)"],
    ["Customer relationship", "Owned by the OTA", "Owned by the reseller"],
    ["Commercial policy", "Set by the OTA", "Set by the reseller, on their own client base"],
    ["Growth ceiling", "Limited to booking volume through the OTA", "Reseller can build and scale an independent B2B business"],
    ["Technology", "Booking engine only", "Booking engine + content + white-label platform"],
  ];

  useJsonLd("vs-ota-breadcrumb", breadcrumbSchema("Sellvago vs B2B OTA", "/sellvago-vs-b2b-ota"));
  useJsonLd(
    "vs-ota-faq",
    faqSchema([
      [
        "Sellvago vs B2B OTA — What's the difference?",
        "A traditional B2B OTA sells inventory to resellers under the OTA's own brand and commercial terms. Sellvago is infrastructure for building your own travel business: resellers own their brand, their customer relationship and their commercial policy, and use Sellvago's content, technology and booking engine underneath.",
      ],
      [
        "In short — is Sellvago just a supplier?",
        "No. A B2B OTA is a supplier. Sellvago is infrastructure for building your own travel business.",
      ],
    ])
  );

  return (
    <PageShell
      testId="vs-ota-page"
      overline="Answer-first · Comparison"
      title="A B2B OTA is a supplier. Sellvago is infrastructure for building your own travel business."
      subtitle="Both give resellers access to travel inventory. The difference is who owns the brand, the customer relationship, and the growth ceiling."
    >
      <div className="glass-dark rounded-sm overflow-hidden" data-testid="vs-ota-comparison-table">
        <div className="grid grid-cols-3 bg-white/[0.04] text-white text-xs uppercase tracking-[0.15em] font-bold">
          <div className="p-5">Dimension</div>
          <div className="p-5 border-l border-white/10">Typical B2B OTA</div>
          <div className="p-5 border-l border-white/10">Sellvago</div>
        </div>
        {rows.map(([dim, ota, sv], idx) => (
          <div
            key={idx}
            className="grid grid-cols-3 text-sm border-t border-white/10"
            data-testid={`vs-ota-row-${idx}`}
          >
            <div className="p-5 font-semibold text-white" data-speakable>{dim}</div>
            <div className="p-5 border-l border-white/10 text-slate-400 leading-relaxed" data-speakable>{ota}</div>
            <div className="p-5 border-l border-white/10 text-slate-200 leading-relaxed" data-speakable>{sv}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 bg-gradient-to-br from-[#00172D] to-[#970C10] rounded-sm p-8 text-white" data-testid="vs-ota-in-short">
        <p className="text-xs uppercase tracking-[0.2em] font-bold mb-3">In short</p>
        <p className="text-xl md:text-2xl font-heading font-bold leading-snug" data-speakable>
          A B2B OTA is a supplier. Sellvago is infrastructure for building your own travel business.
        </p>
      </div>

      <div className="mt-12 flex flex-wrap gap-4">
        <CTA to="/how-white-label-works" label="See how white-label reselling works" testId="vs-ota-white-label-cta" />
        <CTA to="/contact" label="Talk to sales" testId="vs-ota-contact-cta" />
      </div>

      <RelatedNav current="/sellvago-vs-b2b-ota" />
    </PageShell>
  );
}

// ==================================================================
// Page 3 — How white-label travel reselling works
// ==================================================================
export function HowWhiteLabelWorks() {
  usePageMeta(
    "How white-label travel reselling works — Sellvago",
    "A travel reseller signs up for Sellvago's B2B white-label solution. Sellvago provides the booking technology, content and infrastructure, branded to the reseller's own identity. The reseller sets pricing, markups and terms; Sellvago operates invisibly underneath."
  );

  useJsonLd("how-white-label-breadcrumb", breadcrumbSchema("How white-label reselling works", "/how-white-label-works"));
  useJsonLd(
    "how-white-label-faq",
    faqSchema([
      [
        "How does white-label travel reselling work with Sellvago?",
        "A travel reseller signs up for Sellvago's B2B white-label solution. Sellvago provides the booking technology, content, and infrastructure, branded to the reseller's own identity. The reseller sets their own commercial policy — pricing, markups, and terms — for their own client base. The reseller's customers interact with the reseller's brand throughout; Sellvago operates invisibly as the technology layer underneath. The reseller grows their own business and client relationships, powered by Sellvago's platform.",
      ],
      [
        "Who owns the customer relationship in a Sellvago white-label?",
        "The reseller. Customers interact with the reseller's brand throughout the journey — search, quote, ticket and support. Sellvago is the invisible technology and content layer underneath.",
      ],
      [
        "Who sets pricing and commercial policy?",
        "The reseller. Pricing, markups, refund policy and terms are all set by the reseller on their own client base.",
      ],
    ])
  );

  const steps = [
    {
      icon: Users,
      title: "1. Reseller signs up",
      text: "A travel reseller signs up for Sellvago's B2B white-label solution and passes standard due-diligence checks.",
    },
    {
      icon: Layers,
      title: "2. Sellvago provides the stack",
      text: "Sellvago provides the booking technology, content and infrastructure, branded to the reseller's own identity.",
    },
    {
      icon: Wallet,
      title: "3. Reseller sets commercial policy",
      text: "The reseller sets their own pricing, markups and terms for their own client base.",
    },
    {
      icon: ShieldCheck,
      title: "4. Customers see the reseller's brand",
      text: "The reseller's customers interact with the reseller's brand throughout. Sellvago operates invisibly underneath.",
    },
    {
      icon: Rocket,
      title: "5. Reseller grows an independent business",
      text: "The reseller builds real client relationships and grows their own book of business, powered by Sellvago's platform.",
    },
  ];

  return (
    <PageShell
      testId="how-white-label-page"
      overline="Answer-first · How it works"
      title="How white-label travel reselling works."
      subtitle="A travel reseller signs up for Sellvago's B2B white-label solution. Sellvago provides the booking technology, content and infrastructure — branded to the reseller's own identity — while the reseller sets their own pricing and grows their own business."
    >
      <ol className="space-y-4" data-testid="how-white-label-steps">
        {steps.map((s, i) => (
          <li key={i} className="glass-dark rounded-sm p-7 flex gap-5" data-testid={`how-white-label-step-${i}`}>
            <s.icon className="w-6 h-6 text-[#E8767A] shrink-0 mt-1" />
            <div>
              <h3 className="font-heading font-bold text-white text-lg" data-speakable>{s.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed" data-speakable>{s.text}</p>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-12 flex flex-wrap gap-4">
        <CTA to="/white-label-portal" label="See the white-label portal" testId="how-wl-portal-cta" />
        <CTA to="/contact" label="Talk to sales" testId="how-wl-contact-cta" />
      </div>

      <RelatedNav current="/how-white-label-works" />
    </PageShell>
  );
}

// ==================================================================
// Page 4 — How resellers grow their own brand with Sellvago
// ==================================================================
export function HowResellersGrow() {
  usePageMeta(
    "How resellers grow their own brand with Sellvago",
    "Resellers on Sellvago aren't just placing bookings — they're building an independent business. The white-label solution lets top resellers operate under their own name and commercial policy while relying on Sellvago's technology and content."
  );

  useJsonLd("how-resellers-grow-breadcrumb", breadcrumbSchema("How resellers grow with Sellvago", "/how-resellers-grow"));
  useJsonLd(
    "how-resellers-grow-faq",
    faqSchema([
      [
        "How do resellers grow their own brand with Sellvago?",
        "Sellvago's brand promise is growth: resellers who use the platform aren't just placing bookings, they're building an independent business. The white-label solution lets top-performing resellers operate under their own name and commercial policy while relying on Sellvago for the underlying technology and content — the same model that lets a small agency compete credibly with much larger players, without having to build booking technology themselves.",
      ],
      [
        "Can a small agency really compete with much larger players?",
        "Yes. That's the point of Sellvago's model. The underlying booking technology and content are the same whether you're a solo reseller or a national agency — the difference is what you build on top of it under your own brand.",
      ],
    ])
  );

  const pillars = [
    { icon: Sparkles, title: "Your brand, front and centre", text: "Every touchpoint — search, quote, ticket, support — carries the reseller's brand, not Sellvago's." },
    { icon: LineChart, title: "Your own P&L", text: "Resellers set pricing and margin. Growth compounds into their own book, not someone else's marketplace." },
    { icon: Compass, title: "Content that sells for you", text: "Airline pages, route pages and campaign landing pages — the content library is available to your storefront out of the box." },
    { icon: Building2, title: "Compete on infrastructure, not headcount", text: "The same booking technology powers a solo reseller and a national agency. You never build ticketing plumbing yourself." },
  ];

  return (
    <PageShell
      testId="how-resellers-grow-page"
      overline="Answer-first · Growth"
      title="Resellers aren't placing bookings on Sellvago — they're building a business."
      subtitle="The white-label solution lets top-performing resellers operate under their own name and commercial policy while relying on Sellvago for the underlying technology and content."
    >
      <div className="grid md:grid-cols-2 gap-5" data-testid="how-resellers-grow-pillars">
        {pillars.map((p, i) => (
          <div key={i} className="glass-dark rounded-sm p-7" data-testid={`how-resellers-grow-pillar-${i}`}>
            <p.icon className="w-6 h-6 text-[#E8767A] mb-4" />
            <h3 className="font-heading font-bold text-white text-lg" data-speakable>{p.title}</h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed" data-speakable>{p.text}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 bg-gradient-to-br from-[#00172D] to-[#970C10] rounded-sm p-8 text-white" data-testid="how-resellers-grow-promise">
        <p className="text-xs uppercase tracking-[0.2em] font-bold mb-3">Brand promise</p>
        <p className="text-xl md:text-2xl font-heading font-bold leading-snug" data-speakable>
          Resellers who use Sellvago aren&apos;t just placing bookings — they&apos;re building an independent business.
        </p>
      </div>

      <div className="mt-12 flex flex-wrap gap-4">
        <CTA to="/white-label-portal" label="Explore the white-label solution" testId="how-grow-portal-cta" />
        <CTA to="/signup" label="Apply as a reseller" testId="how-grow-signup-cta" />
      </div>

      <RelatedNav current="/how-resellers-grow" />
    </PageShell>
  );
}
